import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import DonutChart from './components/DonutChart';
import AccountantModal from './components/AccountantModal';

const fmt = (n) => '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function TaxCalculator() {
  const [income, setIncome] = useState('');
  const [pension, setPension] = useState('');
  const [reliefType, setReliefType] = useState('');
  const [reliefAmount, setReliefAmount] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setResults(null);
    setLoading(true);
    try {
      const response = await axios.post('/api/calculateTax', {
        income: parseFloat(income),
        pensionContribution: parseFloat(pension) || 0,
        claimBackReliefType: reliefType || null,
        claimBackAmount: reliefAmount ? parseFloat(reliefAmount) : null,
      });
      const { tax, nationalInsurance } = response.data;
      const pensionAmt = parseFloat(income) * ((parseFloat(pension) || 0) / 100);
      const takeHome = parseFloat(income) - tax - nationalInsurance - pensionAmt;
      const taxableIncome = Math.max(0, parseFloat(income) - pensionAmt - (parseFloat(reliefAmount) || 0));
      setResults({ income: parseFloat(income), tax, ni: nationalInsurance, pensionAmt, takeHome, taxableIncome });
    } catch {
      setError('Error calculating tax. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  }

  const donutSlices = results ? [
    { label: 'Take Home', color: '#4f46e5', pct: (results.takeHome / results.income) * 100, val: results.takeHome },
    { label: 'Income Tax', color: '#818cf8', pct: (results.tax / results.income) * 100, val: results.tax },
    { label: 'Nat. Insurance', color: '#c7d2fe', pct: (results.ni / results.income) * 100, val: results.ni },
    ...(results.pensionAmt > 0 ? [{ label: 'Pension', color: '#e0e7ff', pct: (results.pensionAmt / results.income) * 100, val: results.pensionAmt }] : []),
  ] : [];

  return (
    <div className="calc-page page">
      <Helmet>
        <title>Income Tax Calculator 2025/26 — TaxClip</title>
        <meta name="description" content="Enter your salary and instantly see your income tax, National Insurance, pension deductions and take-home pay for the 2025/26 UK tax year." />
        <link rel="canonical" href="https://taxclip.co.uk/calculator" />
        <meta property="og:title" content="Income Tax Calculator 2025/26 — TaxClip" />
        <meta property="og:description" content="Enter your salary and instantly see your income tax, National Insurance, pension deductions and take-home pay for the 2025/26 UK tax year." />
        <meta property="og:url" content="https://taxclip.co.uk/calculator" />
      </Helmet>
      <div className="calc-header">
        <h1 className="calc-title">Income Tax Calculator</h1>
        <p className="calc-subtitle">UK 2025/26 tax year · Income tax &amp; National Insurance</p>
      </div>

      <div className="calc-layout">
        {/* Form */}
        <form className="calc-form-card" onSubmit={handleSubmit}>
          <div className="field-group">
            <label className="field-label" htmlFor="income">Annual Income</label>
            <div className="input-wrap">
              <span className="input-prefix">£</span>
              <input id="income" className="field-input has-prefix" type="number" min="0"
                placeholder="55,000" value={income}
                onChange={e => setIncome(e.target.value)} required />
            </div>
            <span className="field-hint">Your gross salary before any deductions</span>
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="pension">Pension Contribution</label>
            <div className="input-wrap">
              <input id="pension" className="field-input has-suffix" type="number" min="0" max="100" step="0.1"
                placeholder="5" value={pension}
                onChange={e => setPension(e.target.value)} required />
              <span className="input-suffix">%</span>
            </div>
            <span className="field-hint">Employee pension as a % of salary</span>
          </div>

          <hr className="form-divider" />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="optional-section-title">Claim back relief <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional</span></div>

            <div className="field-group">
              <label className="field-label" htmlFor="reliefType">
                Relief type <span className="field-optional">Optional</span>
              </label>
              <select id="reliefType" className="field-input" value={reliefType}
                onChange={e => setReliefType(e.target.value)}>
                <option value="">— No relief claim —</option>
                <option value="uniforms_workclothing_tools">Uniforms, work clothing &amp; tools</option>
                <option value="vehicles">Vehicles used for work</option>
                <option value="travel_overnight">Travel &amp; overnight expenses</option>
                <option value="professional_fees">Professional fees &amp; subscriptions</option>
                <option value="buying_other_equipment">Buying other equipment</option>
                <option value="other_expenses">Other expenses</option>
              </select>
            </div>

            {reliefType && (
              <div className="field-group">
                <label className="field-label" htmlFor="reliefAmt">Relief amount</label>
                <div className="input-wrap">
                  <span className="input-prefix">£</span>
                  <input id="reliefAmt" className="field-input has-prefix" type="number" min="0"
                    placeholder="0" value={reliefAmount}
                    onChange={e => setReliefAmount(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {error && <div className="error-alert">{error}</div>}

          <button className="calc-btn" type="submit" disabled={loading}>
            {loading ? 'Calculating…' : 'Calculate my tax'}
          </button>
        </form>

        {/* Results */}
        <div className="results-panel">
          {!results ? (
            <div className="results-placeholder">
              <div className="results-placeholder-icon">🧮</div>
              <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, color: 'var(--navy)', fontSize: '.95rem' }}>
                Your results will appear here
              </div>
              <div className="results-placeholder-text">
                Fill in your income details on the left and press <em>Calculate my tax</em>
              </div>
            </div>
          ) : (
            <>
              <div className="results-hero">
                <div className="results-hero-label">Annual Take-Home Pay</div>
                <div className="results-hero-amount">{fmt(results.takeHome)}</div>
                <div className="results-hero-monthly">{fmt(results.takeHome / 12)} / month</div>
              </div>

              <div className="donut-section">
                <div className="donut-wrap">
                  <DonutChart slices={donutSlices} size={120} stroke={20} />
                  <div className="donut-center">
                    <div className="donut-center-label">income</div>
                    <div className="donut-center-val">{fmt(results.income)}</div>
                  </div>
                </div>
                <div className="donut-legend">
                  {donutSlices.map(s => (
                    <div key={s.label} className="legend-item">
                      <div className="legend-dot" style={{ background: s.color }} />
                      <div className="legend-name">{s.label}</div>
                      <div className="legend-amount">{fmt(s.val)}</div>
                      <div className="legend-pct">{s.pct.toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="results-breakdown">
                <div className="breakdown-header">Breakdown</div>
                {[
                  { label: 'Gross income', note: 'Before deductions', val: fmt(results.income), neg: false },
                  ...(results.pensionAmt > 0 ? [{ label: 'Pension contribution', note: `${pension}% of salary`, val: `−${fmt(results.pensionAmt)}`, neg: true }] : []),
                  ...(parseFloat(reliefAmount) > 0 ? [{ label: 'Claim back relief', note: reliefType.replace(/_/g, ' '), val: `−${fmt(parseFloat(reliefAmount))}`, neg: true }] : []),
                  { label: 'Taxable income', note: 'After allowances', val: fmt(results.taxableIncome), neg: false },
                  { label: 'Personal allowance', note: '2025/26', val: '£12,570', neg: false },
                  { label: 'Income Tax', note: 'PAYE', val: `−${fmt(results.tax)}`, neg: true },
                  { label: 'National Insurance', note: 'Employee contributions', val: `−${fmt(results.ni)}`, neg: true },
                ].map((row, i) => (
                  <div key={i} className={`breakdown-item${row.neg ? ' breakdown-item-negative' : ''}`}>
                    <div>
                      <div className="breakdown-item-label">{row.label}</div>
                      <div className="breakdown-item-note">{row.note}</div>
                    </div>
                    <div className="breakdown-item-val">{row.val}</div>
                  </div>
                ))}
              </div>

              <button className="find-accountant-btn" onClick={() => setShowModal(true)}>
                🧑‍💼 Find an accountant
              </button>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <AccountantModal
          onClose={() => setShowModal(false)}
          income={results?.income}
          tax={results?.tax}
          nationalInsurance={results?.ni}
        />
      )}
    </div>
  );
}

export default TaxCalculator;
